import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  CATEGORY,
  COUNTRY,
  DISCLOSURE,
  REQUIRED_BY,
  SOURCE,
} from 'constants/formFields';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import i18n from 'i18n';
import { useCreateDisclosureMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateQuestionForm from './hooks/useCreateQuestionForm';

import type IDisclosureBody from 'store/slices/materialAssessment/interfaces/IDisclosureBody';
import type IFieldError from 'store/types/IFieldError';
import type { TDisclosuresCreateSchema } from 'utils/validators';
import type ICreateQuestion from './interfaces/ICreateQuestion';

import styles from './scss/CreateQuestion.module.scss';

function CreateQuestion({
  open, onClose, title, recordId, setOpenModal, setExpandedRowId, vcmId, onQuestionGroupCreated,
}: ICreateQuestion) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createQuestions] = useCreateDisclosureMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    fieldsName,
  } = useCreateQuestionForm({ isPageLoading: isLoading });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddQuestion = (data: IDisclosureBody) => {
    createQuestions({ maId: recordId, data, vcmId }).then(() => {
      toast.success(i18n.t('notifications.createQuestionGroup'));
      setExpandedRowId(-1);
      setOpenModal('');
      onQuestionGroupCreated?.();
    }).catch((errors) => {
      setFieldsErrors<TDisclosuresCreateSchema>({ errors, setError, fieldsName });
    });
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    const source = data[SOURCE]?.trim();

    onAddQuestion({
      text: data[DISCLOSURE],
      requiredBy: data[REQUIRED_BY] || '',
      countryRegion: data[COUNTRY]?.label || '',
      type: data[CATEGORY]?.value || '',
      ...(source ? { source } : {}),
      date: new Date(),
    });
    onCancel();
  });

  const {
    onFindCountries, onChangeCountry, defaultCountries,
  } = useGeoLocations<TDisclosuresCreateSchema>({ watch, setValue });

  const optionProfession = [
    {
      value: 'environmental',
      label: 'Environmental',
    },
    {
      value: 'social',
      label: 'Social',
    },
  ];
  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={DISCLOSURE}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.disclosure')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.category')}
                placeholder={t('materialityAssessment.placeholder.selectCategory')}
                options={optionProfession}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={REQUIRED_BY}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.requiredBy')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={COUNTRY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.country')}
                placeholder={t('materialityAssessment.placeholder.selectCountry')}
                value={value?.value ? value : null}
                onChange={(newValue) => {
                  onChange(newValue);
                  onChangeCountry();
                }}
                error={error?.message}
                loadOptions={onFindCountries}
                defaultOptions={defaultCountries}
                isSearchable
                isAsync
                isClearable
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SOURCE}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value || ''}
                label={t('materialityAssessment.fields.source')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.create')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateQuestion;
